import { useState, useMemo, useEffect, useRef } from "react";
import { Modal, Input, Tag, Button, List, Typography, Alert, Space } from "antd";
import { SearchOutlined, CloseOutlined, PlusOutlined } from "@ant-design/icons";
import useEmployeeService from "../../../../services/PositionAdmin/employeeService";
import { getUnitAcronym, getActiveEarliestUnit } from "../../../../utils/ouberUtils";

const { Text, Title } = Typography;

const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const AdminLinkTripInviteModal = ({ open, onCancel, onAddEmployees, maxSelectable = Infinity, existingPassengerIds = [] }) => {
  const { getAllEmployees, employeeLoading, employeeResponse } = useEmployeeService();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  
  const getAllEmployeesRef = useRef(getAllEmployees);
  useEffect(() => {
    getAllEmployeesRef.current = getAllEmployees;
  }, [getAllEmployees]);

  useEffect(() => {
    if (open) {
      getAllEmployeesRef.current();
      setSelectedEmployees([]);
      setSearchTerm("");
    }
  }, [open]);

  useEffect(() => {
    if (open && searchTerm.length > 2) {
      const handler = setTimeout(() => {
        getAllEmployeesRef.current(searchTerm);
      }, 500);
      return () => clearTimeout(handler);
    }
  }, [searchTerm, open]);

  const allEmployees = useMemo(() => {
    const res = employeeResponse;
    const list = Array.isArray(res) ? res : (res?.data || []);
    return list;
  }, [employeeResponse]);

  const [isExternal, setIsExternal] = useState(false);
  const [manualName, setManualName] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [guestError, setGuestError] = useState("");

  useEffect(() => {
    const trimmedEmail = manualEmail?.trim();
    const trimmedName = manualName?.trim();
    
    if ((trimmedEmail && isValidEmail(trimmedEmail)) || trimmedName) {
      const isDuplicateEmail = trimmedEmail && allEmployees.some(emp => (emp.email || "").toLowerCase() === trimmedEmail.toLowerCase());
      const isDuplicateName = trimmedName && allEmployees.some(emp => {
        const empName = (emp.name || emp.full_name || `${emp.first_name || emp.firstName || ""} ${emp.last_name || emp.lastName || ""}`).trim().toLowerCase();
        return empName === trimmedName.toLowerCase();
      });

      if (isDuplicateEmail) {
        setGuestError("This email belongs to an employee. Please find them in the 'Search Employee' tab.");
      } else if (isDuplicateName) {
        setGuestError("This name matches an existing employee. Please find them in the 'Search Employee' tab.");
      } else {
        setGuestError("");
      }
    } else {
      setGuestError("");
    }
  }, [manualEmail, manualName, allEmployees]);

  const sanitizeInput = (val) => {
    if (typeof val !== 'string') return val;
    return val.trim().replace(/\s+/g, ' ');
  };

  const handleManualAdd = () => {
    const sName = sanitizeInput(manualName);
    const sEmail = (manualEmail || "").trim();

    if (!sName || !isValidEmail(sEmail)) return;

    const duplicateEmail = allEmployees.find(emp => (emp.email || "").toLowerCase() === sEmail.toLowerCase());
    const duplicateName = allEmployees.find(emp => {
      const empName = (emp.name || emp.full_name || `${emp.first_name || emp.firstName || ""} ${emp.last_name || emp.lastName || ""}`).trim().toLowerCase();
      return empName === sName.toLowerCase();
    });

    if (duplicateEmail) {
      setGuestError("This email belongs to an employee. Please find them in the 'Search Employee' tab.");
      return;
    }
    if (duplicateName) {
      setGuestError("This name matches an existing employee. Please find them in the 'Search Employee' tab.");
      return;
    }
    
    if (selectedEmployees.some(emp => (emp.email || "").toLowerCase() === sEmail.toLowerCase() || (emp.name || "").toLowerCase() === sName.toLowerCase())) {
      setGuestError("This person is already in your selected list.");
      return;
    }
    
    if (selectedEmployees.length < (maxSelectable || Infinity)) {
      const extEmp = {
        id: `ext_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
        name: sName,
        email: sEmail,
        is_external: true
      };
      
      setSelectedEmployees(prev => [...prev, extEmp]);
      setManualName("");
      setManualEmail("");
      setGuestError("");
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const searchResults = allEmployees.filter(emp => {
    if (selectedEmployees.find(s => s.id === emp.id)) return false;
    if (existingPassengerIds.some(id => id === emp.id)) return false;
    if (!searchTerm) return true;
    
    const term = searchTerm.toLowerCase();
    const rawName = emp.name || emp.full_name || `${emp.first_name || ""} ${emp.last_name || ""}`.trim();
    const nameStr = rawName.toLowerCase();
    const emailStr = (emp.email || "").toLowerCase();
    
    return nameStr.includes(term) || emailStr.includes(term);
  }).slice(0, searchTerm ? 100 : 20);

  const handleAdd = (id) => {
    const emp = allEmployees.find(e => e.id === id);
    if (!emp) return;
    if (selectedEmployees.length < (maxSelectable || Infinity)) {
      if (selectedEmployees.some(s => s.id === id)) return;
      
      const assignedUnit = getUnitAcronym(getActiveEarliestUnit(emp));
      const empWithUnit = {
        ...emp,
        unit: assignedUnit
      };
      
      setSelectedEmployees(prev => [...prev, empWithUnit]);
    }
  };

  const handleRemove = (id) => {
    setSelectedEmployees(prev => prev.filter(emp => emp.id !== id));
  };

  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onAddEmployees(selectedEmployees);
      setSelectedEmployees([]);
      setSearchTerm("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={500}
      zIndex={1100}
      title={<Title level={4} style={{ margin: 0, color: 'white' }}>Invite Additional Passengers</Title>}
      className="ouber-modal white-close-icon"
      closable={!loading}
    >
      <div style={{ marginTop: "20px" }}>
        {maxSelectable === 0 && (
          <Alert 
            message="Trip is at full capacity. You cannot add more passengers." 
            type="warning" 
            showIcon 
            style={{ marginBottom: '16px' }}
          />
        )}

        <div style={{ marginBottom: '16px' }}>
          <Button 
            type={isExternal ? "default" : "primary"} 
            className={!isExternal ? "ouber-btn-forest" : ""}
            onClick={() => setIsExternal(false)}
            style={{ marginRight: '8px' }}
          >
            Search Employee
          </Button>
          <Button 
            type={isExternal ? "primary" : "default"} 
            className={isExternal ? "ouber-btn-forest" : ""}
            onClick={() => setIsExternal(true)}
          >
            Add Guest
          </Button>
        </div>

        {!isExternal ? (
          <>
            <Input 
              placeholder="Search by name or email" 
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />} 
              value={searchTerm}
              onChange={handleSearch}
              style={{ marginBottom: '16px' }}
            />

            <Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>
              {searchTerm ? 'Search Results' : 'Available Employees (Recent)'}
            </Text>
            <List
              size="small"
              bordered
              rowKey={(item) => item.id}
              loading={employeeLoading}
              dataSource={searchResults}
              renderItem={item => {
                const name = item.name || item.full_name || `${item.first_name || ""} ${item.last_name || ""}`.trim() || "Unknown";
                return (
                  <List.Item
                    actions={[<Button type="text" icon={<PlusOutlined />} onClick={() => handleAdd(item.id)} disabled={selectedEmployees.length >= maxSelectable}>Add</Button>]}
                  >
                    <List.Item.Meta 
                      title={name} 
                      description={
                        <Space direction="vertical" size={0}>
                          <Text type="secondary" style={{ fontSize: '12px' }}>{item.email}</Text>
                          {item.positions && item.positions.length > 0 && (
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                              {getUnitAcronym(getActiveEarliestUnit(item))}
                            </Text>
                          )}
                        </Space>
                      } 
                    />
                  </List.Item>
                );
              }}
              style={{ marginBottom: '24px', maxHeight: '200px', overflowY: 'auto' }}
            />
          </>
        ) : (
          <div style={{ padding: '16px', border: '1px solid #f0f0f0', borderRadius: '8px', marginBottom: '16px' }}>
            <div style={{ marginBottom: '12px' }}>
              <Text style={{ display: 'block', marginBottom: '4px' }}>Full Name <span style={{ color: '#ff4d4f' }}>*</span></Text>
              <Input value={manualName} onChange={e => setManualName(e.target.value)} placeholder="Enter full name" />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <Text style={{ display: 'block', marginBottom: '4px' }}>Email Address <span style={{ color: '#ff4d4f' }}>*</span></Text>
              <Input 
                value={manualEmail} 
                onChange={e => setManualEmail(e.target.value)} 
                placeholder="Enter email address" 
                status={manualEmail && (!isValidEmail(manualEmail) || guestError) ? 'error' : ''}
              />
              {manualEmail && !isValidEmail(manualEmail) && (
                <Text type="danger" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>Please enter a valid email address.</Text>
              )}
              {guestError && (
                <Text type="danger" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>{guestError}</Text>
              )}
            </div>
            <Button 
              block 
              type="primary" 
              className="ouber-btn-forest" 
              onClick={handleManualAdd}
              disabled={!manualName || !isValidEmail(manualEmail) || selectedEmployees.length >= (maxSelectable || Infinity)}
            >
              Add to Selected
            </Button>
          </div>
        )}

        {selectedEmployees.length > 0 && (
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>Selected for Trip</Text>
            <List
              size="small"
              bordered
              dataSource={selectedEmployees}
              style={{ maxHeight: '200px', overflowY: 'auto' }}
              renderItem={item => {
                const name = item.name || item.full_name || `${item.first_name || ""} ${item.last_name || ""}`.trim() || "Unknown";
                return (
                  <List.Item
                    actions={[<Button type="text" danger icon={<CloseOutlined />} onClick={() => handleRemove(item.id)} />]}
                  >
                    <List.Item.Meta 
                      title={<span>{name} {item.is_external && <Tag color="orange" style={{ fontSize: '10px', marginLeft: '4px' }}>Guest</Tag>}</span>} 
                      description={item.email} 
                    />
                  </List.Item>
                );
              }}
            />
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "24px" }}>
          <Button onClick={onCancel} disabled={loading}>Cancel</Button>
          <Button type="primary" className="ouber-btn-forest" onClick={handleSubmit} disabled={selectedEmployees.length === 0} loading={loading}>
            Add to Passengers ({selectedEmployees.length})
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AdminLinkTripInviteModal;
